/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import axios from "../../../util/axiosconfig";
import { Toast } from "primereact/toast";
import { Check } from "lucide-react";
import { showError, showSuccess } from "../../../util/ShowStatus";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { User } from "../Invoice Management Module/UserManagement/UserManagement";
import { Dropdown } from "primereact/dropdown";
import {
  getErrorMessage,
  getSuccessMessage,
} from "../../../helper/getResponseMessage";

interface Permission {
  permissionID: number;
  permission: string;
  isUserAssigned: boolean;
  description?: string; // Optional description for better context
  dateAdded?: string; // Track when isUserAssigned was added
}

interface Menu {
  menuID: number;
  name: string;
  permissions: Permission[];
  description?: string; // Optional description
  dateAdded?: string;
  isActive?: boolean; // To handle menu visibility
}

interface Module {
  moduleID: number;
  displayName: string;
  menus: Menu[];
  description?: string;
  dateAdded?: string;
  isActive?: boolean;
}

const RolesAndPermissions: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveDataLoading, setSaveDataLoading] = useState(false);
  // const [searchTerm, setSearchTerm] = useState("");
  // const [filterActive, setFilterActive] = useState(false);
  const toast = useRef<Toast>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [permissionIDs, setPermissionIDs] = useState<number[]>([]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get("admin/user/list?page=-1");
      setUsers(response.data.result);
      setLoading(false);
    } catch (error) {
      showError("Failed to fetch users", toast);
      setLoading(false);
    }
  };

  const fetchRolesAndPermissions = async () => {
    try {
      // In real implementation, fetch from API
      setLoading(true);
      const response = await axios.post("modules/permissions", {
        userID: selectedUser.userID,
      });
      setModules(response.data.result);

      const permissionIDs = response.data.result.flatMap((module: Module) =>
        module.menus.flatMap((menu) =>
          menu.permissions
            .filter((permission) => permission.isUserAssigned === true)
            .map((permission) => permission.permissionID)
        )
      );
      setPermissionIDs(permissionIDs);

      setLoading(false);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage, toast);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchRolesAndPermissions();
    }
  }, [selectedUser]);

  const handlePermissionChange = (permisionID: number) => {
    setPermissionIDs((prevPermissionIDs) => {
      if (prevPermissionIDs.includes(permisionID)) {
        return prevPermissionIDs.filter((id) => id !== permisionID);
      } else {
        return [...prevPermissionIDs, permisionID];
      }
    });
  };

  const formatPermissionName = (name: string) => {
    return name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // const filterModules = (modules: Module[]) => {
  //   return modules.filter((module) => {
  //     if (!filterActive || module.isActive) {
  //       const moduleMatches = module.displayName
  //         .toLowerCase()
  //         .includes(searchTerm.toLowerCase());
  //       const filteredMenus = module.menus.filter((menu) => {
  //         if (!filterActive || menu.isActive) {
  //           const menuMatches = menu.name
  //             .toLowerCase()
  //             .includes(searchTerm.toLowerCase());
  //           const filteredPermissions = menu.permissions.filter(
  //             (isUserAssigned) =>
  //               isUserAssigned.permission
  //                 .toLowerCase()
  //                 .includes(searchTerm.toLowerCase())
  //           );
  //           return menuMatches || filteredPermissions.length > 0;
  //         }
  //         return false;
  //       });
  //       return moduleMatches || filteredMenus.length > 0;
  //     }
  //     return false;
  //   });
  // };

  const submitPrivilege = async () => {
    try {
      setSaveDataLoading(true);

      const response = await axios.post("modules/set-user-permissions", {
        permissions: permissionIDs,
        userID: selectedUser.userID,
      });
      const successMessage = getSuccessMessage(response);
      showSuccess(successMessage, toast);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showError(errorMessage, toast);
    } finally {
      setSaveDataLoading(false);
    }
  };

  // const filteredModules = filterModules(modules);

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <Toast ref={toast} />

      <div className="tw-bg-white tw-shadow-sm">
        <div className="tw-max-w-7xl tw-mx-auto tw-px-4 tw-py-6 sm:tw-px-6 lg:tw-px-8">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-space-y-4 sm:tw-space-y-0">
            <div>
              <h1 className="tw-text-2xl tw-font-bold tw-text-gray-700">
                User Permissions
              </h1>
            </div>
            <Dropdown
              value={selectedUser}
              // options={users.map(user => ({
              // ...user,
              // displayName: `${user.displayName} (${user.roleDisplayName})`
              // }))} // Add role name to user display name
              options={users}
              optionLabel="displayName"
              placeholder="Select a User"
              onChange={(e) => setSelectedUser(e.value)}
              className="tw-w-72"
              filter
            />
          </div>
        </div>
      </div>
      {/* Header section */}
      {/* <div className="tw-bg-transparent tw-p-6">
        <div className="tw-mb-6">
        <div className="tw-mb-[-10px]">
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-700">
            User Permissions
          </h1>
          <p className="tw-text-gray-600">
            Manage access controls and permissions for different roles
          </p>
        </div>

        <div className="tw-mb-6 tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row tw-justify-between">
          <div className="tw-flex-1 tw-max-w-md">
            <InputText
              type="text"
              placeholder="Search modules, menus, or permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tw-w-full tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-focus:ring-2 tw-focus:ring-blue-500 tw-focus:border-blue-500"
            />
          </div>
        </div>
        </div>
      </div> */}

      {/* Scrollable content section */}
      {loading ? (
        <div className="tw-flex  tw-items-center tw-justify-center tw-h-64">
          {/* <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-t-2 tw-border-b-2 tw-border-blue-500"></div> */}
          <ProgressSpinner />
        </div>
      ) : (
        selectedUser && (
          <div>
            <div className="tw-flex-1">
              <div className="tw-max-w-7xl tw-mx-auto tw-p-6 tw-space-y-6">
                {modules.map((module) => (
                  <div
                    key={module.moduleID}
                    className="tw-bg-white tw-rounded-lg tw-border-2 tw-border-[#152846] tw-overflow-hidden"
                  >
                    <div className="tw-bg-[#152846] tw-px-6 tw-py-4 tw-flex tw-justify-between tw-items-center">
                      <div>
                        <h2 className="tw-text-lg tw-font-bold tw-text-white">
                          {module.displayName}
                        </h2>
                        {module.description && (
                          <p className="tw-text-sm tw-text-gray-300">
                            {module.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="tw-divide-y tw-divide-gray-200">
                      {module.menus.map((menu, menuIndex) => (
                        <div
                          key={menu.menuID}
                          className={`tw-p-6 ${
                            !menu.isActive ? "tw-opacity-75" : ""
                          }`}
                        >
                          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                            <div>
                              <h3 className="tw-text-lg tw-font-bold">
                                {menu.name}
                              </h3>
                              {menu.description && (
                                <p className="tw-text-sm tw-text-gray-500">
                                  {menu.description}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                            {menu.permissions.map((permission: any) => (
                              <div
                                key={permission.permissionID}
                                className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-200 tw-rounded-lg hover:tw-bg-gray-300 tw-transition-colors"
                              >
                                <div className="tw-flex tw-flex-col">
                                  <span className="tw-text-sm tw-font-medium">
                                    {formatPermissionName(
                                      permission.permission
                                    )}
                                  </span>
                                </div>
                                <label>
                                  <InputSwitch
                                    className="tw-text-blue-900"
                                    checked={permissionIDs.includes(
                                      permission.permissionID
                                    )}
                                    onChange={() =>
                                      handlePermissionChange(
                                        permission.permissionID
                                      )
                                    }
                                  />
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save button - fixed on large screens, normal flow on small screens */}
            <div className="tw-relative 2xl:tw-fixed 2xl:tw-bottom-0 2xl:tw-right-0 tw-bg-transparent">
              <div className="tw-max-w-7xl tw-mx-auto tw-p-6 tw-flex tw-justify-end">
                <Button
                  onClick={submitPrivilege}
                  disabled={saveDataLoading}
                  className="tw-inline-flex tw-items-center tw-mr-[-10px] tw-px-[10px] tw-py-2 tw-border tw-border-transparent tw-text-base tw-font-medium tw-rounded-md tw-shadow-sm tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  {saveDataLoading ? (
                    <>
                      <div className="tw-animate-spin tw-mr-2 tw-h-4 tw-w-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="tw-w-5 tw-h-5 tw-mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default RolesAndPermissions;
